import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { type IActions } from '../../../interface/IAction';
import { type IConsentLogData } from '../../../interface/IAgents';
import { type IStates } from '../../../interface/IReducer';
import ConsentItem from './ConsentItem';
import { saveConsent } from './consentStorage';
import {
	ArrowIcon,
	BellIcon,
	BuildingIcon,
	ChevronIcon,
	DatabaseIcon,
	FaceScanIcon,
	LockIcon,
	ScaleIcon,
	ShieldLockIcon,
	SpinnerIcon,
	TargetIcon,
} from './icons';
import LegalDocumentModal from './LegalDocumentModal';
import {
	CONSENT_OPTIONS,
	CONSENT_SUMMARY,
	DATA_CONSENT_POLICY_VERSION,
	POLICY_DOCUMENTS,
	type PolicyDocumentLink,
} from './policy';
import {
	consentSectionStyles,
	containerStyles,
	footerStyles,
	getButtonStyles,
	getChevronStyles,
	getSummaryHeaderStyles,
	headerIconStyles,
	headerStyles,
	microcopyStyles,
	overlayStyles,
	policyLinkButtonStyles,
	policyLinksRowStyles,
	sectionLabelStyles,
	subtitleStyles,
	summaryBodyStyles,
	summaryGroupStyles,
	summaryHeadingStyles,
	summaryIconTileStyles,
	summarySectionStyles,
	titleStyles,
} from './styled';

interface DataConsentModalProps {
	isOpen: boolean;
	// Optional: the trusted-agent invitee verifies no phone before consenting.
	verifiedPhone?: string;
	electionId?: number;
	partyId?: number;
	actions?: IActions;
	states?: IStates;
	onConsent: (consentReference: string | null) => void;
}

type ConsentId = 'dataProcessing' | 'biometricVerification' | 'futureContact';

const SUMMARY_ICONS: Record<string, React.ReactNode> = {
	what: <DatabaseIcon />,
	why: <TargetIcon />,
	who: <BuildingIcon />,
	rights: <ScaleIcon />,
};

const OPTION_ICONS: Record<ConsentId, React.ReactNode> = {
	dataProcessing: <LockIcon />,
	biometricVerification: <FaceScanIcon />,
	futureContact: <BellIcon />,
};

const DataConsentModal: React.FC<DataConsentModalProps> = ({
	isOpen,
	verifiedPhone,
	electionId,
	partyId,
	actions,
	states,
	onConsent,
}) => {
	const containerRef = useRef<HTMLDivElement>(null);

	// `actions` from DataWrapper is a new reference every render, so it can't go
	// in an effect dependency array (it would loop). Read it through a ref.
	const actionsRef = useRef(actions);
	actionsRef.current = actions;

	const [consents, setConsents] = useState<Record<ConsentId, boolean>>({
		dataProcessing: false,
		biometricVerification: false,
		futureContact: false,
	});
	const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
	const [submitting, setSubmitting] = useState(false);
	const [activeDoc, setActiveDoc] = useState<PolicyDocumentLink | null>(null);

	const legalDocs = states?.election?.getLegalDocuments;
	const legalLoading = states?.election?.getLegalDocuments_Loading;
	const legalError = states?.election?.getLegalDocuments_Error;
	const activeDocument =
		activeDoc && Array.isArray(legalDocs) ? legalDocs.find((d) => d.documentType === activeDoc.documentType) : undefined;

	const allRequiredChecked = consents.dataProcessing && consents.biometricVerification;
	const canSubmit = allRequiredChecked && !submitting;

	const toggleConsent = useCallback((id: ConsentId) => {
		setConsents((prev) => ({ ...prev, [id]: !prev[id] }));
	}, []);

	const toggleSection = useCallback((id: string) => {
		setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
	}, []);

	useEffect(() => {
		if (isOpen) document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = '';
		};
	}, [isOpen]);

	useEffect(() => {
		if (isOpen && containerRef.current) containerRef.current.focus();
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key !== 'Tab') return;
			const focusable = containerRef.current?.querySelectorAll<HTMLElement>('[role="checkbox"], button:not(:disabled), a[href]');
			if (!focusable || focusable.length === 0) return;

			const first = focusable[0];
			const last = focusable[focusable.length - 1];

			if (e.shiftKey && document.activeElement === first) {
				e.preventDefault();
				last.focus();
			} else if (!e.shiftKey && document.activeElement === last) {
				e.preventDefault();
				first.focus();
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [isOpen]);

	// Prefetch the legal documents when the gate opens so the reader dialog is
	// ready the moment a user taps a "Read the full …" link.
	useEffect(() => {
		if (isOpen) actionsRef.current?.getLegalDocumentsAction?.();
	}, [isOpen]);

	const handleSubmit = () => {
		if (!canSubmit) return;
		setSubmitting(true);

		const record: IConsentLogData = {
			phoneNumber: (verifiedPhone ?? '').startsWith('+') ? (verifiedPhone ?? '').slice(1) : (verifiedPhone ?? ''),
			electionId,
			partyId,
			policyVersion: DATA_CONSENT_POLICY_VERSION,
			consents: {
				dataProcessing: consents.dataProcessing,
				biometricVerification: consents.biometricVerification,
				futureContact: consents.futureContact,
			},
			acceptedAt: new Date().toISOString(),
			clientContext: {
				userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
				language: typeof navigator !== 'undefined' ? navigator.language : '',
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? '',
			},
		};

		const proceed = (consentReference: string | null) => {
			saveConsent({
				policyVersion: DATA_CONSENT_POLICY_VERSION,
				consentReference,
				consents: record.consents,
			});
			setSubmitting(false);
			onConsent(consentReference);
		};

		if (!actions?.logConsentAction) {
			proceed(null);
			return;
		}

		actions.logConsentAction(
			record,
			(res) => proceed(res?.consentReference ?? null),
			(err) => {
				// Don't block onboarding if the audit endpoint isn't reachable yet —
				// the consent still rides along on the final Volunteer submission.
				console.error('Consent logging failed; proceeding with local record.', err);
				proceed(null);
			}
		);
	};

	if (!isOpen) return null;

	return (
		<>
			<div style={overlayStyles}>
				<div
					ref={containerRef}
					role='dialog'
					aria-modal='true'
					aria-labelledby='data-consent-title'
					tabIndex={-1}
					style={containerStyles}
				>
					{/* Header */}
					<div style={headerStyles}>
						<div style={headerIconStyles}>
							<ShieldLockIcon />
						</div>
						<h2
							id='data-consent-title'
							style={titleStyles}
						>
							Your data, your consent
						</h2>
						<p style={subtitleStyles}>Before we collect anything, here&apos;s exactly what we do with your information.</p>
					</div>

					{/* Informed-consent summary */}
					<div style={summarySectionStyles}>
						<p style={sectionLabelStyles}>How we handle your data</p>
						{CONSENT_SUMMARY.map((section) => {
							const open = !!openSections[section.id];
							return (
								<div
									key={section.id}
									style={summaryGroupStyles}
								>
									<button
										type='button'
										style={getSummaryHeaderStyles(open)}
										aria-expanded={open}
										aria-controls={`summary-body-${section.id}`}
										onClick={() => toggleSection(section.id)}
									>
										<span style={summaryIconTileStyles}>{SUMMARY_ICONS[section.id]}</span>
										<span style={summaryHeadingStyles}>{section.heading}</span>
										<span style={getChevronStyles(open)}>
											<ChevronIcon />
										</span>
									</button>
									{open && (
										<p
											id={`summary-body-${section.id}`}
											style={summaryBodyStyles}
										>
											{section.body}
										</p>
									)}
								</div>
							);
						})}
					</div>

					{/* Full-policy links — open the document in an in-app reader dialog */}
					<div style={policyLinksRowStyles}>
						{POLICY_DOCUMENTS.map((link) => (
							<button
								key={link.documentType}
								type='button'
								style={policyLinkButtonStyles}
								onClick={() => setActiveDoc(link)}
							>
								Read the full {link.label}
							</button>
						))}
					</div>

					{/* Consent options */}
					<div style={consentSectionStyles}>
						<p style={sectionLabelStyles}>Please confirm</p>
						{CONSENT_OPTIONS.map((option, index) => (
							<ConsentItem
								key={option.id}
								icon={OPTION_ICONS[option.id]}
								title={option.title}
								description={option.description}
								required={option.required}
								isChecked={consents[option.id]}
								onToggle={() => toggleConsent(option.id)}
								delay={120 + index * 70}
							/>
						))}
					</div>

					{/* Footer */}
					<div style={footerStyles}>
						<button
							type='button'
							disabled={!canSubmit}
							style={getButtonStyles(canSubmit)}
							onClick={handleSubmit}
							aria-label='I Consent and Continue'
						>
							{submitting ? (
								<>
									<SpinnerIcon />
									Saving your consent…
								</>
							) : (
								<>
									I Consent &amp; Continue
									<ArrowIcon />
								</>
							)}
						</button>
						<p style={microcopyStyles}>
							By continuing you confirm the above is accurate and that you&apos;re providing this data to be used as described.
							You can withdraw consent at any time by contacting us.
						</p>
					</div>
				</div>
			</div>

			<LegalDocumentModal
				isOpen={!!activeDoc}
				title={activeDoc?.title ?? ''}
				document={activeDocument}
				loading={legalLoading}
				error={legalError}
				onRetry={() => actions?.getLegalDocumentsAction?.()}
				onClose={() => setActiveDoc(null)}
			/>
		</>
	);
};

export default DataConsentModal;
